import type { BindableQrTargetType, QrTargetType } from '@prospero/shared';
import { createContext, useContext } from 'react';

export interface QrBindingsBatchState {
  targetType: BindableQrTargetType;
  coveredTargetIds: ReadonlySet<string>;
  /** QR per target id; `undefined` until the batch has answered at least once. */
  bindingsByTargetId: ReadonlyMap<string, string | null> | undefined;
  /** The last batch request failed (bindings may still be known from an earlier answer). */
  isError: boolean;
}

/**
 * State of the enclosing `QrBindingsPrefetch` batch. A covered target reads its
 * binding from here and never issues its own request, so a list of N cards
 * costs one request instead of N.
 */
export const QrBindingsBatchContext = createContext<QrBindingsBatchState | null>(null);

/** The enclosing batch when it covers this target, otherwise `null`. */
export function useCoveringQrBindingsBatch(
  targetType: QrTargetType,
  targetId: string,
): QrBindingsBatchState | null {
  const batchState = useContext(QrBindingsBatchContext);
  if (!batchState || batchState.targetType !== targetType) {
    return null;
  }
  return batchState.coveredTargetIds.has(targetId) ? batchState : null;
}
